// HILDR — IRFMI: lagførers minimumsordre.
//
// KILDE: GRUS (Grunnleggende soldatferdigheter, Heimevernet, HVSKS v1.0 des.
// 2018) s. 51-52; kilde der: Reglementet for fotlaget s. 47.
//
// GRUS om når den brukes: «IRFMI er et stridsledelsesverktøy som lagføreren kan
// benytte når situasjonen tilsier en endring i plan eller handlemåte. Denne
// ordren er ment brukt underveis i en operasjon eller i umiddelbar forkant […]
// dersom lagfører ikke har tid til å gi en lagførerordre. Denne må kunnes utenat
// av lagfører.»
//
// HVORFOR DETTE RETTER EN FEIL HOS OSS: ordremotoren vår genererte fempunktsordre
// for ALLE nivåer. På lagsnivå er det galt — der er IRFMI formatet, og hele
// poenget er at den er KORT nok å gi muntlig når det ikke er tid til mer. En
// fempunktsordre til et lag i stridskontakt er ubrukelig uansett hvor korrekt
// den er.
//
// Punktene, med GRUS' egne styrende spørsmål:
//   I  Innledning        — hva skal skje (f.eks. hvilken stridsdrill)
//   R  Retning           — til fienden / til egne / for fremrykning, med avstand i METER
//                          → HVOR SKAL LAGET og HVA ER DER?
//   F  Formasjon         — formasjon ved fremrykning, eller stilling for gjenger/makkerpar
//                          → HVORDAN SER LAGET UT I LENDET?
//   M  Metode/kort plan  — → HVA ER LAGFØRERS PLAN?
//   I  Innbruddssted,    — brukes ETTER BEHOV, ingen fast rekkefølge internt
//      ildledelse,         → HVOR SKAL LAGET BRYTE INN? ILDLEDELSE? NÅR IVERKSETTES?
//      iverksettelse
import { buildDtg, stridsEmphasis } from '@skade/contracts';
import { compassSector, bearingDeg, haversineKm } from './geo.js';
import { ildapningTilTekst } from './ild.js';
/**
 * Velg ordreformat ut fra echelon. GRUS gjelder lag/tropp; fempunktsordren hører
 * til stabsnivået (HPBP, kompani/eskadron og over). Ukjent nivå → fempunktsordre,
 * fordi en for FYLDIG ordre er mindre skadelig enn en for tynn.
 */
export function velgOrdreformat(echelon) {
    const e = (echelon ?? '').toLowerCase().trim();
    if (e === 'lag' || e === 'gjeng' || e === 'makkerpar' || e === 'tropp')
        return 'irfmi';
    return 'fempunktsordre';
}
const IRFMI_STD_IVERKSETT = 'Iverksett!';
/** Bygg en IRFMI. Deterministisk: samme input → samme ordre og id. */
export function buildIrfmi(inn) {
    const idBase = (inn.enhetId ?? inn.til).replace(/\s+/g, '-');
    return {
        ordreId: `IRFMI-${idBase}-${inn.tsMs}`,
        dtg: buildDtg(inn.tsMs, inn.sone ?? 'A'),
        fra: (inn.fra ?? 'LF').trim(),
        til: inn.til.trim(),
        innledning: stridsEmphasis(inn.innledning.trim()),
        retning: {
            tilFienden: inn.retning?.tilFienden?.trim() ? stridsEmphasis(inn.retning.tilFienden.trim()) : undefined,
            tilEgne: inn.retning?.tilEgne?.trim() ? stridsEmphasis(inn.retning.tilEgne.trim()) : undefined,
            forFremrykning: inn.retning?.forFremrykning?.trim() ? stridsEmphasis(inn.retning.forFremrykning.trim()) : undefined,
        },
        formasjon: (inn.formasjon?.trim() || 'Formasjon som nå.'),
        metode: (inn.metode?.trim() || 'Kort plan gis muntlig.'),
        innbruddssted: inn.innbruddssted?.trim() || undefined,
        // Ildledelse: bruk ildåpningsbestemmelsen når den er oppgitt — det er den
        // doktrinære måten, framfor fritekst som kan bli tvetydig om ildåpning.
        ildledelse: inn.ildapning ? ildapningTilTekst(inn.ildapning) : (inn.ildledelse?.trim() || undefined),
        iverksettelse: inn.iverksettelse?.trim() || IRFMI_STD_IVERKSETT,
    };
}
/**
 * Bygg IRFMI for en FORFLYTNING direkte av to punkt — den vanligste bruken på
 * kartet. Retningen oppgis som himmelretning + avstand i METER, slik GRUS krever
 * (ikke km, ikke grader alene). Speiler GRUS' eget eksempel:
 * «R: Til høydedraget 600m i front».
 */
export function irfmiForForflytning(opts) {
    const km = haversineKm(opts.fraPunkt, opts.tilPunkt);
    const meter = Math.round(km * 1000);
    const sektor = compassSector(bearingDeg(opts.fraPunkt, opts.tilPunkt));
    const mal = opts.malbeskrivelse?.trim() || 'ny stilling';
    return buildIrfmi({
        til: opts.til,
        fra: opts.fra,
        tsMs: opts.tsMs,
        sone: opts.sone,
        enhetId: opts.enhetId,
        innledning: opts.innledning?.trim() || 'Forflytning',
        retning: { forFremrykning: `Til ${mal} ${meter} m mot ${sektor}` },
        formasjon: opts.formasjon,
        metode: opts.metode,
        ildapning: opts.ildapning,
    });
}
/**
 * IRFMI som tekst for muntlig fremføring. Punkter uten innhold KUTTES — GRUS er
 * eksplisitt om at det siste I-punktet «benyttes ut fra behov», og eksempelet
 * viser «Innbruddssted: Ikke relevant - kuttes». En ordre med tomme punkter er
 * tregere å lese opp, og det er nettopp tid IRFMI finnes for å spare.
 */
export function irfmiTilTekst(o) {
    const l = [`IRFMI ${o.ordreId}`, `DTG ${o.dtg} · FRA ${o.fra} · TIL ${o.til}`, ''];
    l.push(`I — INNLEDNING: ${o.innledning}`);
    const r = [];
    if (o.retning.tilFienden)
        r.push(`fienden: ${o.retning.tilFienden}`);
    if (o.retning.tilEgne)
        r.push(`egne: ${o.retning.tilEgne}`);
    if (o.retning.forFremrykning)
        r.push(`fremrykning: ${o.retning.forFremrykning}`);
    l.push(`R — RETNING: ${r.length ? r.join(' · ') : 'som nå'}`);
    l.push(`F — FORMASJON: ${o.formasjon}`);
    l.push(`M — METODE: ${o.metode}`);
    const siste = [];
    if (o.innbruddssted)
        siste.push(`innbruddssted: ${o.innbruddssted}`);
    if (o.ildledelse)
        siste.push(`ildledelse: ${o.ildledelse}`);
    if (o.iverksettelse)
        siste.push(`iverksettelse: ${o.iverksettelse}`);
    if (siste.length)
        l.push(`I — ${siste.join(' · ')}`);
    return l.join('\n');
}
/** Ett-linjes sammendrag til kart-popup og logg. */
export function irfmiSammendrag(o) {
    const r = o.retning.forFremrykning ?? o.retning.tilFienden ?? 'som nå';
    return `${o.dtg} · ${o.til} · ${o.innledning} — ${r}`;
}
/** Hva mangler før ordren kan gis? Tomt = klar. */
export function manglerVedIrfmi(o) {
    const m = [];
    if (!o.innledning.trim())
        m.push('I — innledning mangler: hva skal skje?');
    if (!o.retning.tilFienden && !o.retning.tilEgne && !o.retning.forFremrykning) {
        m.push('R — ingen retning oppgitt: hvor skal laget, og hva er der?');
    }
    if (!o.formasjon.trim())
        m.push('F — formasjon mangler: hvordan ser laget ut i lendet?');
    if (!o.metode.trim())
        m.push('M — metode mangler: hva er lagførers plan?');
    return m;
}
//# sourceMappingURL=irfmi.js.map